"use client";

import { useState } from "react";
import {
  Notice,
  type Option,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import { BondResults } from "@/components/tools/accounting/bond-results";
import { CurrencyField } from "@/components/tools/finance/shared";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  BASES,
  type Basis,
  type BondTerms,
  bondYield,
  FREQUENCIES,
  type Frequency,
  parseIsoDate,
  serial,
} from "@/lib/tool-kit/accounting/bonds";

/** English strings. A translated content file overrides these through `ui`. */
export const BOND_YIELD_CALCULATOR_STRINGS = {
  inputsHeading: "The bond",
  currency: "Currency",
  plainNumbers: "No currency (plain numbers)",
  modeLabel: "Work out",
  modePrice: "Price from yield",
  modeYield: "Yield to maturity from price",
  settlement: "Settlement date (when you buy)",
  maturity: "Maturity date",
  couponRate: "Annual coupon rate (%)",
  yieldInput: "Yield to maturity (% a year)",
  priceInput: "Clean price per 100 of par",
  redemption: "Redemption value per 100 of par",
  face: "Face (par) amount you hold",
  frequencyLabel: "Coupon payments per year",
  annual: "1 — annual",
  semiannual: "2 — semiannual",
  quarterly: "4 — quarterly",
  basisLabel: "Day-count basis",
  basis0: "0 — 30/360, US (NASD)",
  basis1: "1 — actual/actual",
  basis2: "2 — actual/360",
  basis3: "3 — actual/365",
  basis4: "4 — 30/360, European",
  convention:
    "Prices are quoted per 100 of par and are clean (without accrued interest), as Excel's PRICE and YIELD return them. The day-count basis sets how accrued days and the coupon period are counted, as in Excel.",
  resultsHeading: "Results",
  cleanPrice: "Clean price per 100",
  dirtyPrice: "Dirty (invoice) price per 100",
  ytm: "Yield to maturity",
  currentYield: "Current yield",
  macaulay: "Macaulay duration (years)",
  modified: "Modified duration",
  perHundred:
    "Current yield is the annual coupon divided by the clean price. Dirty price is clean price plus accrued interest.",
  faceHeading: "For a face amount of {face}",
  itemColumn: "Item",
  amountColumn: "Amount",
  cleanAmount: "Clean price",
  accruedAmount: "Accrued interest",
  invoiceAmount: "Invoice amount (clean + accrued)",
  couponAmount: "Coupon each payment date",
  workingHeading: "The working",
  workFacts:
    "{n} coupons remain. The coupon period runs {previous} to {next}: A = {a} days accrued, E = {e} days in the period, DSC = {dsc} days to the next coupon.",
  workAccrued:
    "Accrued interest = coupon × A ÷ E = {coupon} × {a} ÷ {e} = {accrued} per 100",
  workPrice:
    "Clean price = present value of all cash flows − accrued interest = {dirty} − {accrued} = {clean}",
  lastPeriodNote:
    "Only one coupon remains, so the price uses simple interest over the final period, as Excel does.",
  shockHeading: "If the yield changes",
  shockChange: "Change (percentage points)",
  shockYield: "New yield",
  shockPrice: "New clean price",
  shockActual: "Price change",
  shockEstimate: "Estimate from modified duration",
  flowsHeading: "Cash flows per 100 of par",
  flowNumber: "Coupon",
  flowDate: "Date",
  flowAmount: "Cash flow",
  flowYears: "Years from settlement",
  flowPv: "Present value",
  flowWeighted: "Years × present value",
  needDates:
    "Enter a settlement date and a maturity date, with settlement before maturity.",
  tooLong: "Enter a bond with no more than 100 years to maturity.",
  needNumbers:
    "Enter a coupon rate of 0 or more, a yield of −100% or more, a redemption value above zero and a face amount above zero.",
  needPrice: "Enter a price above zero.",
  noYield:
    "No yield between −100% a year and 10,000% reproduces that price. Check the price and the dates.",
} as const;

export type BondYieldCalculatorStrings = typeof BOND_YIELD_CALCULATOR_STRINGS;

type Mode = "price" | "yield";
const MAX_YEARS = 100;

interface Form {
  mode: Mode;
  settlement: string;
  maturity: string;
  rate: string;
  yld: string;
  price: string;
  redemption: string;
  face: string;
  frequency: string;
  basis: string;
}

type Parsed =
  | { terms: BondTerms; y: number; face: number }
  | { error: keyof BondYieldCalculatorStrings };

function parseForm(form: Form): Parsed {
  const settlement = parseIsoDate(form.settlement);
  const maturity = parseIsoDate(form.maturity);
  if (!(settlement && maturity) || serial(settlement) >= serial(maturity)) {
    return { error: "needDates" };
  }
  if (serial(maturity) - serial(settlement) > MAX_YEARS * 366) {
    return { error: "tooLong" };
  }
  const rate = parseNumberInput(form.rate);
  const redemption = parseNumberInput(form.redemption);
  const face = parseNumberInput(form.face);
  if (
    rate === null ||
    rate < 0 ||
    redemption === null ||
    redemption <= 0 ||
    face === null ||
    face <= 0
  ) {
    return { error: "needNumbers" };
  }
  const terms: BondTerms = {
    settlement,
    maturity,
    rate: rate / 100,
    redemption,
    frequency: Number(form.frequency) as Frequency,
    basis: Number(form.basis) as Basis,
  };
  if (form.mode === "price") {
    const yld = parseNumberInput(form.yld);
    if (yld === null || yld / 100 <= -terms.frequency) {
      return { error: "needNumbers" };
    }
    return { terms, y: yld / 100, face };
  }
  const price = parseNumberInput(form.price);
  if (price === null || price <= 0) {
    return { error: "needPrice" };
  }
  const y = bondYield(terms, price);
  return y === null ? { error: "noYield" } : { terms, y, face };
}

export default function BondYieldCalculatorWidget() {
  const strings = useToolUi(BOND_YIELD_CALCULATOR_STRINGS);
  const [currency, setCurrency] = useState("USD");
  const [form, setForm] = useState<Form>({
    mode: "price",
    settlement: "2008-02-15",
    maturity: "2017-11-15",
    rate: "5.75",
    yld: "6.5",
    price: "94.63436162",
    redemption: "100",
    face: "1000",
    frequency: "2",
    basis: "0",
  });
  const update = (patch: Partial<Form>) =>
    setForm((old) => ({ ...old, ...patch }));
  const modes: Option<Mode>[] = [
    { value: "price", label: strings.modePrice },
    { value: "yield", label: strings.modeYield },
  ];
  const frequencyLabels: Record<Frequency, string> = {
    1: strings.annual,
    2: strings.semiannual,
    4: strings.quarterly,
  };
  const frequencies: Option[] = FREQUENCIES.map((f) => ({
    value: String(f),
    label: frequencyLabels[f],
  }));
  const basisLabels: Record<Basis, string> = {
    0: strings.basis0,
    1: strings.basis1,
    2: strings.basis2,
    3: strings.basis3,
    4: strings.basis4,
  };
  const bases: Option[] = BASES.map((b) => ({
    value: String(b),
    label: basisLabels[b],
  }));
  const parsed = parseForm(form);

  return (
    <div className="space-y-6">
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.inputsHeading}</h2>
        <SelectField
          id="bond-mode"
          label={strings.modeLabel}
          onChange={(mode) => update({ mode })}
          options={modes}
          value={form.mode}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <CurrencyField
            id="bond-currency"
            label={strings.currency}
            onChange={setCurrency}
            plainLabel={strings.plainNumbers}
            value={currency}
          />
          <TextField
            id="bond-face"
            label={strings.face}
            numeric
            onChange={(face) => update({ face })}
            value={form.face}
          />
          <TextField
            id="bond-settlement"
            label={strings.settlement}
            onChange={(settlement) => update({ settlement })}
            type="date"
            value={form.settlement}
          />
          <TextField
            id="bond-maturity"
            label={strings.maturity}
            onChange={(maturity) => update({ maturity })}
            type="date"
            value={form.maturity}
          />
          <TextField
            id="bond-rate"
            label={strings.couponRate}
            numeric
            onChange={(rate) => update({ rate })}
            value={form.rate}
          />
          {form.mode === "price" ? (
            <TextField
              id="bond-yield"
              label={strings.yieldInput}
              numeric
              onChange={(yld) => update({ yld })}
              value={form.yld}
            />
          ) : (
            <TextField
              id="bond-price"
              label={strings.priceInput}
              numeric
              onChange={(price) => update({ price })}
              value={form.price}
            />
          )}
          <TextField
            id="bond-redemption"
            label={strings.redemption}
            numeric
            onChange={(redemption) => update({ redemption })}
            value={form.redemption}
          />
          <SelectField
            id="bond-frequency"
            label={strings.frequencyLabel}
            onChange={(frequency) => update({ frequency })}
            options={frequencies}
            value={form.frequency}
          />
          <SelectField
            id="bond-basis"
            label={strings.basisLabel}
            onChange={(basis) => update({ basis })}
            options={bases}
            value={form.basis}
          />
        </div>
        <p className={HINT}>{strings.convention}</p>
      </section>
      {"error" in parsed ? (
        <Notice>{t(strings[parsed.error], {})}</Notice>
      ) : (
        <BondResults
          currency={currency}
          face={parsed.face}
          strings={strings}
          terms={parsed.terms}
          y={parsed.y}
        />
      )}
    </div>
  );
}
